import { GLOBAL } from 'saddlebag-browser';
import { logOperationalEvent, logWarn } from 'saddlebag-logger';
import observer from 'saddlebag-observer';

import {
  getConsentCookieName,
  getConsumer,
  invalidateWillShowBannerCache,
  isKetchPsidSyncEnabled,
  isPurposeScopedIdServiceEnabled,
} from './cmp-state';
import {
  COMPONENT,
  KETCH_EVENT_BRIDGE_SETUP_FAILURE,
} from './constants/events';
import {
  markForwardSyncSucceeded,
  shouldSuppressShowExperience,
} from './forward-sync';
import {
  syncKetchToGdpr,
  syncKetchTcStringToGdpr,
} from './ketch-consent-migration';
import { getKetchCookieValue } from './ketch-cookie-utils';
import { refreshPurposeScopedIdKetch } from './purpose-scoped-id-service';

// Type for Ketch SDK function
type KetchFunction = (
  command: string,
  event: string | ((...args: unknown[]) => void),
  callback?: (data?: unknown) => void,
) => void;

/**
 * Callbacks for Ketch consent experience visibility events
 */
export interface KetchEventBridgeCallbacks {
  /** Callback when consent banner should be shown */
  onShowConsentExperience?: () => void;
  /** Callback when consent banner should be hidden */
  onHideConsentExperience?: () => void;
}

/**
 * Gets the window object from saddlebag-browser
 * @returns Window object or empty object if not available
 */
function getWindow():
  | (Window & { ketch?: KetchFunction })
  | Record<string, never> {
  return GLOBAL.getWindow() as
    | (Window & { ketch?: KetchFunction })
    | Record<string, never>;
}

let bridgeInitialized = false;
let bannerVisible = false;
let storedCallbacks: KetchEventBridgeCallbacks = {};

/**
 * Handles consent update from 'cachedConsent' event.
 * Reads the _swb_consent_ cookie directly rather than using the callback data,
 * as this is the authoritative source expected by the purpose-scoped-id-service.
 *
 * @param consentData - The consent object from Ketch callback (used for reverse gdpr sync)
 */
function handleConsentUpdate(consentData?: unknown): void {
  // Refresh the cached banner decision before notifying subscribers,
  // so showCookieBanner() returns the up-to-date value when they re-check.
  invalidateWillShowBannerCache();

  // Publish tracking-preferences-changed for UI updates
  observer.publish('tracking-preferences-changed');

  if (isPurposeScopedIdServiceEnabled() && isKetchPsidSyncEnabled()) {
    const consentCookieValue = getKetchCookieValue(getConsentCookieName());
    if (consentCookieValue) {
      refreshPurposeScopedIdKetch(consentCookieValue);
    }
  }

  // Reverse sync: keep gdpr cookie in sync with Ketch for rollback safety
  syncKetchToGdpr(consentData);

  // When our forward setConsent has just landed (cachedConsent with source
  // `router.setConsent`), mark forward-sync succeeded and defensively emit
  // `hideExperience` — by this point Ketch may have already evaluated
  // `_calculateNeedsConsent` against the empty consent state and decided to
  // show the banner. The bridge's `willShowExperience` listener short-circuits
  // when forward sync is in-progress / succeeded, but emitting hideExperience
  // here is belt-and-braces in case Ketch's internal banner state machine
  // needs an explicit hide signal.
  const source =
    typeof consentData === 'object' && consentData !== null
      ? (consentData as { context?: { source?: unknown } }).context?.source
      : undefined;
  if (source === 'router.setConsent') {
    markForwardSyncSucceeded();
    try {
      const w = getWindow();
      if (typeof w.ketch === 'function') {
        w.ketch('emit', 'hideExperience');
      }
    } catch {
      // hide emit is best-effort; primary suppression is in willShowExperience
    }
  }
}

/**
 * Sets up event bridge from Ketch SDK to saddlebag-observer.
 *
 * Bridges:
 * - Ketch 'cachedConsent' → 'tracking-preferences-changed' + refreshPurposeScopedIdKetch (if enabled)
 * - Ketch 'willShowExperience' (consent only) → onShowConsentExperience callback
 * - Ketch 'hideExperience' (when banner visible) → onHideConsentExperience callback
 *
 * We use 'cachedConsent' event to trigger PSID sync because it allows us to read the
 * _swb_consent_ cookie directly, which is the authoritative source expected by
 * the purpose-scoped-id-service.
 *
 * Should be called after Ketch SDK loads (in loadCMPSDK onLoad callback).
 * This function is idempotent - multiple calls will only initialize once.
 *
 * @param callbacks - Optional callbacks for consent experience visibility events
 */
export function setupKetchEventBridge(
  callbacks?: KetchEventBridgeCallbacks,
): void {
  // Store callbacks even if bridge is already initialized
  // This allows late-registered callbacks to be stored
  if (callbacks) {
    storedCallbacks = { ...storedCallbacks, ...callbacks };
  }

  if (bridgeInitialized) {
    return;
  }

  const browserWindow = getWindow();
  if (typeof browserWindow.ketch !== 'function') {
    logOperationalEvent({
      eventName: KETCH_EVENT_BRIDGE_SETUP_FAILURE,
      component: COMPONENT,
      consumer: getConsumer(),
    });
    logWarn({
      component: COMPONENT,
      message: `${KETCH_EVENT_BRIDGE_SETUP_FAILURE}: Ketch SDK not loaded`,
    });
    return;
  }

  const { ketch } = browserWindow;

  // Subscribe to 'cachedConsent' event - fires when consent is updated by the user
  ketch('on', 'cachedConsent', (consentData?: unknown) => {
    handleConsentUpdate(consentData);
  });

  // Subscribe to Ketch's `protocols` event for synchronous tcString delivery.
  // Ketch fires this with `{ tcf, gpp?, usps? }` after its GVL fetch
  // completes, which is the correct moment to mirror the IAB tcString into
  // the legacy gdpr cookie for rollback safety. Reading the tcString from
  // localStorage during `cachedConsent` would race the SDK on first load.
  ketch('on', 'protocols', (data?: unknown) => {
    syncKetchTcStringToGdpr(data);
  });

  // Track banner visibility via willShowExperience and hideExperience events.
  // Only fire callbacks for consent experience, NOT preference center.
  //
  // Forward-sync suppression: when our migration is in progress / has
  // succeeded, we deliberately do NOT propagate `onShowConsentExperience` to
  // the host. The Ketch SDK fires `willShowExperience` BEFORE our deferred
  // `setConsent` (queued via `once('identities')`) lands. Suppressing the
  // host callback keeps the React `<ConsentBanner>` from mounting; we also
  // emit `hideExperience` so any internal Ketch state machine wraps up.
  ketch('on', 'willShowExperience', (experienceType?: unknown) => {
    if (experienceType !== 'experiences.consent') {
      return;
    }
    if (shouldSuppressShowExperience()) {
      try {
        const w = getWindow();
        if (typeof w.ketch === 'function') {
          w.ketch('emit', 'hideExperience');
        }
      } catch {
        // best-effort
      }
      return;
    }
    bannerVisible = true;
    storedCallbacks.onShowConsentExperience?.();
  });

  ketch('on', 'hideExperience', () => {
    if (bannerVisible) {
      bannerVisible = false;
      storedCallbacks.onHideConsentExperience?.();
    }
  });

  bridgeInitialized = true;
}

/**
 * Returns whether the consent banner is currently visible.
 * This is tracked via Ketch's willShowExperience and hideExperience events.
 *
 * @returns true if banner is visible, false otherwise
 */
export function isBannerVisible(): boolean {
  return bannerVisible;
}

/**
 * Reset bridge state.
 * FOR TESTING ONLY - do not use in production code.
 */
export function resetEventBridge(): void {
  bridgeInitialized = false;
  bannerVisible = false;
  storedCallbacks = {};
}
